import { Button } from "@dmca/frontend/components/button";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import { PageHeader } from "@dmca/frontend/components/nav";
import {
  checkPermissionsInLoader,
  useAppPermissions,
  useDebounce,
} from "@dmca/frontend/hooks";
import {
  ProjectJobLineItem,
  type ProductionProjectContextItem,
  OverallReportsPanel,
  type JobSort,
  type JobSortField,
  cycleJobSort,
  PieChartFilterSelection,
} from "@dmca/frontend/widgets/projects";
import {
  Box,
  Card,
  Container,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, Suspense, useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useList } from "react-use";
import { useDrawerLayoutContext } from "./-DrawerLayoutContext";
import { toast } from "react-toastify";

export const Route = createFileRoute("/_authed/_drawerLayout/ProjectOverview")({
  component: ProjectOverview,
  loader: async ({ context }) =>
    await checkPermissionsInLoader(
      "ProductionJob::ViewProjectDashboard",
      context
    ),
  head: () => ({
    meta: [
      {
        title: "Project Overview | DNA Dashboard",
      },
    ],
  }),
});

function ProjectOverview() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const canUpdateJob = useAppPermissions()("ProductionJob::UpdateJob");
  const updateJob = useMutation(
    trpc.productionJob.updateProductionJob.mutationOptions()
  );

  const { data: locations, isLoading: cellsIsLoading } = useQuery(
    trpc.cell.getAll.queryOptions()
  );
  const { data: projectStorageRows = [], isLoading: storageIsLoading } =
    useQuery(
      trpc.productionStorage.getAll.queryOptions({
        projectStatusOnly: true,
        includeRemoved: false,
      })
    );

  const [selectedProjects, setSelectedProjects] = useState<string[] | null>(
    null
  );
  const [requestedProjects, setRequestedProjects] = useState<string[] | null>(
    null
  );
  const [workOrderSearch, setWorkOrderSearch] = useState("");
  const [selectedLocationFilters, setSelectedLocationFilters] = useState<
    PieChartFilterSelection[]
  >([]);
  const selectedLocationIds = Array.from(
    new Set(
      selectedLocationFilters.flatMap((selection) =>
        selection.type === "cell"
          ? [selection.locationId]
          : selection.locationIds
      )
    )
  );
  // Debounce list queries so typing does not rekey the infinite query every keystroke.
  const [debouncedWorkOrderSearch, setDebouncedWorkOrderSearch] = useDebounce(
    "",
    400
  );
  useEffect(() => {
    setDebouncedWorkOrderSearch(workOrderSearch);
  }, [workOrderSearch, setDebouncedWorkOrderSearch]);

  const [jobSort, setJobSort] = useState<JobSort>([
    { id: "priority", desc: false },
    { id: "dueDate", desc: false },
  ]);
  const [openResetConfirmationModal, setOpenResetConfirmationModal] =
    useState(false);
  const [
    showChangeProjectConfirmationModal,
    setShowChangeProjectConfirmationModal,
  ] = useState(false);

  const {
    data: jobs,
    isLoading: jobsIsLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    trpc.productionJob.getAllWithLocationInfinite.infiniteQueryOptions(
      {
        cursor: 0,
        limit: 20,
        projectNames: selectedProjects ?? undefined,
        searchTerm: debouncedWorkOrderSearch,
        locationIds: selectedLocationIds.length
          ? selectedLocationIds
          : undefined,
        sort: jobSort,
      },

      {
        enabled: selectedProjects === null || selectedProjects.length > 0,
        getNextPageParam: (lastPage) => {
          if (lastPage.hasAdditionalData) {
            return lastPage.page + 1;
          }
          return undefined;
        },
      }
    )
  );

  const isLoading = cellsIsLoading || jobsIsLoading || storageIsLoading;

  const listInitialization = useList<ProductionProjectContextItem>();
  const [updatedPriorityList, { clear }] = listInitialization;

  const updateSelectedProjects = useCallback(
    (projects: string[] | null) => {
      setSelectedProjects(projects);
      setSelectedLocationFilters([]);
      clear();
    },
    [clear]
  );

  useDrawerLayoutContext({
    sx: { display: "none" },
    listItems: [],
    showSearchBar: false,
  });

  const handleProjectChange = useCallback(
    (projects: string[] | null) => {
      if (projects === selectedProjects) return;
      if (
        selectedProjects !== null &&
        projects !== null &&
        projects.length === selectedProjects.length &&
        projects.every((project) => selectedProjects.includes(project))
      )
        return;
      if (updatedPriorityList.length === 0) {
        updateSelectedProjects(projects);
      } else {
        setRequestedProjects(projects);
        setShowChangeProjectConfirmationModal(true);
      }
    },
    [selectedProjects, updatedPriorityList.length, updateSelectedProjects]
  );

  const { data: priorityCounts } = useQuery(
    trpc.productionJob.getJobPriorityCount.queryOptions()
  );

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView]);

  const handleSaveButtonClick = () => {
    updateJob.mutate(
      updatedPriorityList.map((item) => ({
        job: item.job,
        priority: item.priority,
        priorityOverrideLocations: item.priorityOverrideLocations,
        genericTrackingLocations: item.genericTrackingLocations,
        cellCompleteDate: item.cellCompleteDate,
        dueDate: item.dueDate,
        extraInfo: item.extraInfo,
      })),
      {
        onSuccess: () => {
          clear();
          qc.invalidateQueries(
            trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter()
          );
          toast.success("Job updated successfully.");
        },
        onError: (error) => {
          console.error("Failed to update job:", error);
          toast.error(
            `Failed to update job, please try again or contact support.`
          );
        },
      }
    );
  };

  const handleResetConfirmation = () => {
    updatedPriorityList.forEach((item) => item.reset());
    clear();
  };

  const handleSortField = useCallback((field: JobSortField) => {
    setJobSort((current) => cycleJobSort(current, field));
  }, []);

  return (
    <>
      <PageHeader>Project Overview</PageHeader>
      <Container
        maxWidth="xl"
        disableGutters
        sx={(theme) => ({
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        })}
      >
        <Suspense fallback={<LoadingSkeleton />}>
          <OverallReportsPanel
            selectedProjects={selectedProjects}
            onProjectChange={handleProjectChange}
            workOrderSearch={workOrderSearch}
            onWorkOrderSearchChange={setWorkOrderSearch}
            jobSort={jobSort}
            onJobSortChange={setJobSort}
            selectedLocationFilters={selectedLocationFilters}
            onSelectedLocationFiltersChange={setSelectedLocationFilters}
          >
            <Box
              data-testid="projects-job-list"
              aria-busy={
                workOrderSearch !== debouncedWorkOrderSearch || jobsIsLoading
              }
            >
              {jobs?.pages.map((page, i) => {
                return (
                  <Fragment key={i}>
                    {page.data.map((job) => {
                      return (
                        <Fragment key={job.id}>
                          <ProjectJobLineItem
                            job={job}
                            canUpdateJob={canUpdateJob}
                            storageRows={projectStorageRows.filter(
                              (row) => row.jobId === job.id
                            )}
                            locations={locations ?? []}
                            updatedProjectUseList={listInitialization}
                            jobSort={jobSort}
                            onSortField={handleSortField}
                            ref={i === jobs.pages.length - 1 ? ref : undefined}
                          />
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
              {(isLoading || hasNextPage) && <LoadingSkeleton />}
            </Box>
          </OverallReportsPanel>
        </Suspense>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            right: 0,
            width: "100%",
            flexGrow: 0,
            display: "flex",
            flexDirection: "column",
            zIndex: 300,
          }}
        >
          {priorityCounts ? (
            <Card
              sx={{
                p: 1,
                width: "max-content",
                m: 1,
                position: "relative",
                zIndex: 301,
              }}
              elevation={24}
              data-testid="priority-summary-card"
            >
              <Typography variant="subtitle1" data-testid="priority-summary-1">
                # of priority 1 jobs:{" "}
                {priorityCounts.find((x) => x.priority === 1)?.count ?? 0}
              </Typography>
              <Typography variant="subtitle1" data-testid="priority-summary-2">
                # of priority 2 jobs:{" "}
                {priorityCounts.find((x) => x.priority === 2)?.count ?? 0}
              </Typography>
              <Typography variant="subtitle1" data-testid="priority-summary-3">
                # of priority 3 jobs:{" "}
                {priorityCounts.find((x) => x.priority === 3)?.count ?? 0}
              </Typography>
            </Card>
          ) : (
            <div />
          )}

          {canUpdateJob && updatedPriorityList.length > 0 && (
            <Paper
              sx={{
                p: 3,
                m: 0,
                display: "flex",
                justifyContent: "center",
                gap: 3,
                position: "relative",
                boxShadow: "0px 10px 13px 20px rgba(0,0,0,0.3)",
              }}
              elevation={20}
            >
              <Button
                fullWidth
                data-testid="projects-overview-save-button"
                onClick={handleSaveButtonClick}
                disabled={!updatedPriorityList.every((x) => x.isValid)}
              >
                Save
              </Button>
              <Button
                fullWidth
                color="error"
                data-testid="projects-overview-reset-all-btn"
                onClick={() => setOpenResetConfirmationModal(true)}
              >
                Reset All
              </Button>
            </Paper>
          )}
        </Box>
      </Container>

      <ConfirmationModal
        open={openResetConfirmationModal}
        onClose={() => setOpenResetConfirmationModal(false)}
        onConfirm={handleResetConfirmation}
      />
      <ConfirmationModal
        open={showChangeProjectConfirmationModal}
        onClose={() => {
          setShowChangeProjectConfirmationModal(false);
          setRequestedProjects(null);
        }}
        onConfirm={() => {
          updateSelectedProjects(requestedProjects);
          setRequestedProjects(null);
        }}
        message="Your unsaved changes will be lost! Are you sure?"
      />
    </>
  );
}

const LoadingSkeleton = () => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        minHeight: "100px",
        width: "100%",
      }}
    >
      <CircularProgress />
    </Box>
  );
};
